<template>
  <v-container>
    <h1>Advertisements</h1>
    <hr />

    <FeaturedMatchProducts
      v-if="!loading"
      :products="products"
      :adsMatchesLoanNone="adsMatchesLoanNone"
      :adsMatchesLoanConv="adsMatchesLoanConv"
      :adsMatchesLoanSba="adsMatchesLoanSba"
      @create="createAd"
      @remove="removeAd"
    />

    <FeaturedProductsAmortization
      v-if="!loading"
      :products="products"
      :adsAmortization="adsAmortization"
      @create="createAd"
      @remove="removeAd"
    />

    <ArchivedAdsTable
      v-if="!loading"
      :products="products"
    />

    <v-dialog
      max-width="450"
      :value="!!showCreateConfirmDialog"
      @click:outside="showCreateConfirmDialog = null">
      <v-card>
        <v-card-text>
          Are you sure you want to assign the product to this ad slot? This will go into effect immediately.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default-btn text" @click="showCreateConfirmDialog = null">
            Cancel
          </v-btn>
          <v-btn class="default-btn blue" @click="createAd(showCreateConfirmDialog, true)">
            <span>Confirm</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="450"
      :value="!!showRemoveConfirmDialog"
      @click:outside="showRemoveConfirmDialog = null">
      <v-card>
        <v-card-text>
          Are you sure you want to clear the product to this ad slot? This will go into effect immediately.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default-btn text" @click="showRemoveConfirmDialog = null">
            Cancel
          </v-btn>
          <v-btn class="default-btn red" @click="removeAd(showRemoveConfirmDialog, true)">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import ArchivedAdsTable from './ArchivedAdsTable'
import FeaturedMatchProducts from './FeaturedMatchProducts'
import FeaturedProductsAmortization from './FeaturedProductsAmortization'

export default {
  name: 'AdSlots',
  components: {
    ArchivedAdsTable,
    FeaturedMatchProducts,
    FeaturedProductsAmortization,
  },
  data: () => ({
    products: [],
    showCreateConfirmDialog: null,
    showRemoveConfirmDialog: null,
    adsMatchesLoanNone: [],
    adsMatchesLoanConv: [],
    adsMatchesLoanSba: [],
    adsAmortization: [],
    loading: true,
  }),
  computed: {
    ...mapGetters('feathers', ['apiAds', 'apiProducts']),
  },

  mounted () {
    this.retrieveData()
  },

  methods: {
    async retrieveData () {
      this.loading = true

      this.products = await this.apiProducts.find()

      const results = await Promise.all([
        this.apiAds.find({
          query: {
            active: true,
            type: 'match_loan_none',
            $sort: { slotNumber: 1 },
          },
        }),
        this.apiAds.find({
          query: {
            active: true,
            type: 'match_loan_conv',
            $sort: { slotNumber: 1 },
          },
        }),
        this.apiAds.find({
          query: {
            active: true,
            type: 'match_loan_sba',
            $sort: { slotNumber: 1 },
          },
        }),
        this.apiAds.find({
          query: {
            active: true,
            type: 'resource_amortization',
            $sort: { slotNumber: 1 },
          },
        }),
      ])

      this.adsMatchesLoanNone = results[0]
      this.adsMatchesLoanConv = results[1]
      this.adsMatchesLoanSba = results[2]
      this.adsAmortization = results[3]

      this.loading = false
    },

    async createAd (data, confirmed = false) {
      if (!confirmed) {
        this.showCreateConfirmDialog = data
        return
      }

      try {
        const ad = await this.apiAds.create(data)

        if (ad.type === 'match_loan_none') this.adsMatchesLoanNone.push(ad)
        if (ad.type === 'match_loan_conv') this.adsMatchesLoanConv.push(ad)
        if (ad.type === 'match_loan_sba') this.adsMatchesLoanSba.push(ad)
        if (ad.type === 'resource_amortization') this.adsAmortization.push(ad)

        this.showCreateConfirmDialog = null
        this.addSuccessAlert('The ad has been set!')
      } catch (error) {
        this.addErrorAlert(`There was an error setting up the ad: ${error.message}`)
      }
    },

    async removeAd (data, confirmed = false) {
      if (!confirmed) {
        this.showRemoveConfirmDialog = data
        return
      }

      try {
        const ad = await this.apiAds.patch(data.id, {
          active: false,
        })

        if (ad.type === 'match_loan_none') this.adsMatchesLoanNone.splice(data.slotIndex, 1)
        if (ad.type === 'match_loan_conv') this.adsMatchesLoanConv.splice(data.slotIndex, 1)
        if (ad.type === 'match_loan_sba') this.adsMatchesLoanSba.splice(data.slotIndex, 1)
        if (ad.type === 'resource_amortization') this.adsAmortization.splice(data.slotIndex, 1)

        this.showRemoveConfirmDialog = null
        this.addSuccessAlert('The ad has been cleared!')
      } catch (error) {
        this.addErrorAlert(`There was an error clearing the ad: ${error.message}`)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {

  .v-card {
    margin-bottom: 20px;

    .v-card__title {
      align-items: center;
      background-color: $accentBlue;
      color: white !important;
      padding: 10px 20px;
      word-break: normal;

      h2 {
        color: white !important;
        font-family: "Sansation Bold", sans-serif;
      }

      p {
        color: white !important;
      }
    }

    .v-card__text {
      padding-top: 30px;
    }

    h4 {
      margin: 5px 10px 0;
    }
  }

  .v-dialog .v-card {
    margin: 0;
  }

  .input-group {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;

    .v-input, .v-input__slot {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    button {
      box-shadow: $primaryShadow;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin: 0 !important;
    }
  }

  .v-tabs-bar {
    margin-top: -30px !important;
    margin-bottom: 30px !important;
  }

  .v-tabs-bar__content {
    justify-content: flex-start !important;
  }
}
</style>
